import { FC, useCallback, useEffect, useMemo, useState } from "react";

import { AreaChart } from "../../../Charts/AreaChart/AreaChart";
import { AreaLine } from "../../../Charts/AreaChart/AreaChart.type";
import { EUTradeObject } from "../../../Charts/shared/chart.type";
import { FilterDropdown } from "../shared/filter/FilterDropdown";
import { FilterInput } from "../shared/filter/FilterDropdown.type";
import { UiBreakpoints } from "../../../../config/ui/breakpoints";
import { useWindowSize } from "../../../../hooks/useWindowSize";
import { Alert } from "../../../Alert/Alert";
import { useAsyncData } from "../../../../hooks/useAsyncData";

import { euColor, keys, worldColor } from "./EvolutionOfImportAndExportOfHomeAppliancesInEurope.const";
import { getFilteredData, getUnit, groupAreaData } from "./EvolutionOfImportAndExportOfHomeAppliancesInEurope.helper";

import { ExportAndImportInfoBox } from "./ExportAndImportInfoBox/ExportAndImportInfoBox";
import { EvolutionOfImportAndExportOfHomeAppliancesInEuropeProps } from "./EvolutionOfImportAndExportOfHomeAppliancesInEurope.type";

const EvolutionOfImportAndExportOfHomeAppliancesInEurope: FC<EvolutionOfImportAndExportOfHomeAppliancesInEuropeProps> = ({ data }: { data: () => Promise<EUTradeObject[]> }) => {
	const [type, setType] = useState<string>("Export");
	const [category, setCategory] = useState<string>("Total");
	const [unit, setUnit] = useState<string>("valueInEuros");
	const [year, setYear] = useState<number>();

	const [euroDataSet, setEuroDataSet] = useState<EUTradeObject[]>();
	const [nonEuroDataSet, setNonEuroDataSet] = useState<EUTradeObject[]>();
	const [totalNonEuroDataSet, setTotalNonEuroDataSet] = useState<EUTradeObject[]>();

	const { width } = useWindowSize();

	const { data: rawData, loading, error } = useAsyncData<EUTradeObject[]>(data);

	// Fetches all EU27 data, depending on the current filter values
	const selectedEuroData = useMemo(() => {
		if (!euroDataSet || !type || !category || !unit) {
			return;
		}
		return getFilteredData(euroDataSet, type, category, unit);
	}, [category, euroDataSet, type, unit]);

	// Fetches all nonEU data, depending on the current filter values. This dataset is mostly used for the summary screen on the right
	const selectedNonEuroData = useMemo(() => {
		if (!nonEuroDataSet || !type || !category || !unit) {
			return;
		}
		return getFilteredData(nonEuroDataSet, type, category, unit);
	}, [category, nonEuroDataSet, type, unit]);

	// Fetches the sum data of all nonEu data, depending on the current filter values
	const selectedTotalNonEuroData = useMemo(() => {
		if (!totalNonEuroDataSet || !type || !category || !unit) {
			return;
		}
		return getFilteredData(totalNonEuroDataSet, type, category, unit);
	}, [category, totalNonEuroDataSet, type, unit]);

	const chartData = useMemo(() => {
		if (!selectedEuroData || !selectedTotalNonEuroData) {
			return;
		}
		const years = selectedEuroData.map((dataObject) => dataObject.period);

		return years.map((year) => {
			const euYearValue = selectedEuroData.find((euValue) => euValue.period === year);
			const worldYearValue = selectedTotalNonEuroData.find((worldValue) => worldValue.period === year);
			if (!euYearValue || !worldYearValue) {
				return;
			}
			return {
				name: year,
				euTradeValue: (euYearValue as any).value,
				worldTradeValue: (worldYearValue as any).value,
				euColor: euColor,
				worldColor: worldColor,
			};
		}) as AreaLine[];
	}, [selectedEuroData, selectedTotalNonEuroData]);

	const yAxisFormatter = useCallback(
		(tick: number | string) => {
			return `${new Intl.NumberFormat("nl-BE").format(Math.ceil(+tick / 1000000))} M${unit === "valueInEuros" ? "€" : "Kg"}`;
		},
		[unit],
	);

	const handleDropdownChange = useCallback((selectors: FilterInput) => {
		const { type: selectedType, category: selectedCategory, unit: selectedUnit } = selectors;

		setType(selectedType);
		setCategory(selectedCategory);
		setUnit(getUnit(selectedUnit));
	}, []);

	const handleTooltipChange = useCallback((selectedYear: number) => {
		if (!selectedYear) {
			return;
		}
		setYear(selectedYear);
	}, []);

	useEffect(() => {
		if (!rawData) return;

		const { euroData, nonEuroData, totalNonEuroData } = groupAreaData(rawData);

		if (!euroData || !nonEuroData || !totalNonEuroData) return;

		setEuroDataSet(euroData);
		setNonEuroDataSet(nonEuroData);
		setTotalNonEuroDataSet(totalNonEuroData);
	}, [rawData]);

	return (
		<>
			{loading && <p>Loading data...</p>}

			{error && <Alert type="error" message={error} />}

			{!loading && !error && (
				<>
					<div className="mb-10">
						<h3 className="mb=10">Evolution of import and export of home appliances</h3>
					</div>
					<div className="mb-12 md:mb-5">
						<FilterDropdown onChange={handleDropdownChange} />
					</div>
					<div className="relative flex flex-col gap-12 xl:gap-0">
						<div className="w-full">
							{chartData && (
								<AreaChart
									onChange={handleTooltipChange}
									data={chartData}
									keys={keys}
									margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
									height={width >= UiBreakpoints.lg ? 750 : 500}
									XAxisProps={{
										padding: { left: width >= UiBreakpoints["2xl"] ? 50 : 13, right: width >= UiBreakpoints["2xl"] ? 50 : 13 },
										tick: { width: 100, fontSize: width >= UiBreakpoints["2xl"] ? "16px" : "13px" },
										interval: 0,
									}}
									YAxisProps={{
										tickFormatter: yAxisFormatter,
										tick: { width: 100, fontSize: width >= UiBreakpoints["2xl"] ? "16px" : "14px" },
									}}
									stacked
								/>
							)}
						</div>
						<div className="w-full sm:w-3/4 self-center xl:absolute xl:top-6 xl:left-20 xl:w-96 xl:self-auto">
							{year && selectedEuroData && selectedNonEuroData && selectedTotalNonEuroData && (
								<ExportAndImportInfoBox
									period={year}
									euroData={selectedEuroData}
									nonEuroData={selectedNonEuroData}
									worldData={selectedTotalNonEuroData}
									unit={unit}
								/>
							)}
						</div>
					</div>
				</>
			)}
		</>
	);
};

export { EvolutionOfImportAndExportOfHomeAppliancesInEurope };
